import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MarkdownEditorComponent } from '@components/shared/markdown-editor/markdown-editor.component';
import { provideDateFnsDatetimeAdapter } from '@ng-matero/extensions-date-fns-adapter';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { ja } from 'date-fns/locale';
import { MarkdownModule, MarkdownService, provideMarkdown } from 'ngx-markdown';
import { firstValueFrom } from 'rxjs';

/** ダイアログのViewModel */
export type NotificationEditorDialogViewModel = {
  updating: boolean;
  target?: EditedNotificationViewModel;
};

/** 掲示板メッセージのViewModel */
export type EditedNotificationViewModel = {
  /** 掲示板メッセージID */
  messageId?: string;

  /** タイトル */
  title: string;

  /** 内容 */
  content: string;

  /** 投稿日 */
  releaseAt: string;
};

/** 掲示板メッセージのフォームグループ */
type NotificationFormGroup = FormGroup<{
  /** 掲示板メッセージID */
  messageId: FormControl<string>;

  /** タイトル */
  title: FormControl<string>;

  /** 内容 */
  content: FormControl<string>;

  /** 説明 */
  releaseAt: FormControl<Date | undefined>;
}>;

@Component({
  selector: 'app-notification-edit-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatFormField,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIconModule,
    CommonModule,
    MtxDatetimepickerModule,
    MarkdownEditorComponent,
    MarkdownModule,
  ],
  templateUrl: './notification-edit-dialog.component.html',
  styleUrl: './notification-edit-dialog.component.scss',
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsDatetimeAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
        monthInput: 'MM',
        yearInput: 'yyyy',
        timeInput: 'HH:mm',
        datetimeInput: 'yyyy/MM/dd HH:mm',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthInput: 'MM',
        yearInput: 'yyyy',
        timeInput: 'HH:mm',
        datetimeInput: 'yyyy/MM/dd HH:mm',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
        popupHeaderDateLabel: 'yyyy/MM/dd',
      },
    }),
    provideMarkdown(),
    MarkdownService,
  ],
})
export class NotificationEditDialogComponent {
  /** 更新中 */
  public updating: boolean;

  /** 更新前の日時 */
  public releaseAtBeforeUpdating: Date | undefined;

  /** フォームグループ */
  public formGroup: NotificationFormGroup;

  // 投稿済みか
  public isPosted: boolean = false;

  private dateRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const releaseAt = group.get('releaseAt')?.value;
    // 追加ダイアログのバリデーション
    if (!this.updating) {
      return !(releaseAt === null || releaseAt >= new Date())
        ? { dateRange: true }
        : null;
    }
    // 編集ダイアログのバリデーション

    // 未投稿の掲示板メッセージ
    if (
      this.releaseAtBeforeUpdating &&
      this.releaseAtBeforeUpdating >= new Date()
    ) {
      return !(
        releaseAt === null ||
        releaseAt === undefined ||
        releaseAt >= new Date() ||
        // 比較のためにミリ秒を切り捨て
        this.releaseAtBeforeUpdating!.setSeconds(0) === releaseAt.setseconds(0)
      )
        ? { dateRange: true }
        : null;
    }
    // すでに投稿された掲示板メッセージ
    return releaseAt === null ||
      // 比較のためにミリ秒を切り捨て
      !(
        this.releaseAtBeforeUpdating!.setSeconds(0) === releaseAt?.setSeconds(0)
      )
      ? { dateRange: true }
      : null;
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) options: NotificationEditorDialogViewModel,
    private dialogRef: MatDialogRef<NotificationEditDialogComponent>,
    private dialog: MatDialog,
  ) {
    this.updating = options.updating;

    if (this.updating) {
      this.releaseAtBeforeUpdating = options.target?.releaseAt
        ? new Date(options.target.releaseAt)
        : undefined;

      this.isPosted = this.releaseAtBeforeUpdating
        ? this.releaseAtBeforeUpdating <= new Date()
        : false;
    }

    this.formGroup = new FormGroup(
      {
        messageId: new FormControl<string>(options.target?.messageId || '', {
          nonNullable: true,
          validators: [],
        }),
        title: new FormControl<string>(options.target?.title || '', {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(100)],
        }),
        content: new FormControl<string>(options.target?.content || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        releaseAt: new FormControl<Date | undefined>(
          this.releaseAtBeforeUpdating,
          {
            nonNullable: true,
            validators: [],
          },
        ),
      },
      { validators: this.dateRangeValidator },
    );
  }

  /**
   * 送信
   */
  public async submit() {
    const title = this.formGroup.get('title')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `掲示板メッセージ「${title}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    let releaseAt: string;
    if (this.formGroup.get('releaseAt')?.value instanceof Date) {
      releaseAt = this.formGroup
        .get('releaseAt')
        ?.value!.toISOString() as string;
    } else {
      const now = new Date();
      releaseAt = now.toISOString();
    }

    const notification: EditedNotificationViewModel = {
      messageId: this.formGroup.get('messageId')?.value || '',
      title: this.formGroup.get('title')?.value || '',
      content: this.formGroup.get('content')?.value || '',
      releaseAt: releaseAt,
    };
    this.dialogRef.close(notification);
  }

  /** 子コンポーネントからemitされた掲示板メッセージ内容文をformGroupに同期させる */
  public inputContent(content: string): void {
    this.formGroup.get('content')?.setValue(content);
  }
}
